"use client";
import FinansfabrikenMobile from "../assets/Icons/FinansfabrikenMobile.png";
import Image from "next/image";
import { useTranslations } from "next-intl";
import LinkedInIcon from "@/assets/footer/linkdinIcon.svg";
import InstagramIcon from "@/assets/footer/instagramIcon.png";
import FacebookIcon from "@/assets/footer/facebookIcon.png";
import { Link, useRouter, usePathname } from "@/i18n/navigation";
import { sessionStore } from "@/utils/storage";
import {
  ABOUT_SCROLL_INTENT_KEY,
  scrollToAboutMainSection
} from "@/components/AboutUs/client/AboutUsScrollOnLoad.client";

const SOCIAL_LINKS = [
  {
    key: "linkedin",
    href: "https://www.linkedin.com/company/finansfabriken/posts/?feedView=all",
    icon: LinkedInIcon
  },
  {
    key: "instagram",
    href: "https://www.instagram.com/finansfabriken/",
    icon: InstagramIcon
  },
  {
    key: "facebook",
    href: "https://www.facebook.com/finansfabriken",
    icon: FacebookIcon
  }
];

function Footer() {
  const t = useTranslations();
  const router = useRouter();
  const pathname = usePathname();

  // For team/board/owners: scroll to the About Us main content section
  // (skipping the hero). If already within the About Us layout, scroll now;
  // otherwise leave an intent for the layout to handle once it mounts.
  const handleSectionLink = (e, href) => {
    e.preventDefault();
    if (pathname === href) {
      scrollToAboutMainSection();
      return;
    }
    sessionStore.set(ABOUT_SCROLL_INTENT_KEY, "main-section");
    router.push(href);
  };

  return (
    <div className="w-full bg-[url('/FooterMobile.png')] md:bg-[url('/FooterBackground.png')] bg-cover py-8  md:py-12 section-horizontal-padding">
      <div className="max-w-8xl mx-auto">
        <div className="flex flex-row-reverse justify-between md:flex-row gap-8 md:gap-4">
          {/* Logo Section */}
          <div className="w-full flex flex-col items-end md:block basis-2/3 md:w-1/5 lg:w-1/4">
            <div className="block md:hidden w-12">
              <Image
                src={FinansfabrikenMobile}
                alt="Finansfabriken"
                width={112}
                height={40}
                className="w-full h-auto"
              />
            </div>

            {/* Desktop version: shown from md up */}
            <Image
              src="/Icons/Finansfabriken.svg"
              alt="Finansfabriken"
              className="hidden md:block w-28 lg:w-36 xl:w-[190px] xl:h-[70px] 2xl:w-48"
              width={192}
              height={40}
            />
          </div>

          {/* Navigation Links */}
          <div className="w-full md:w-4/5 lg:w-3/4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-8">
              {/* First Column */}
              <div className="text-[#EDD2B9] text-[16px] md:text-[clamp(14px,1.0417vw,20px)]">
                <ul className="flex flex-col">
                  <p className="font-medium">
                    <span className="md:hidden">{t("footer.column1.our_services_short")}</span>
                    <span className="hidden md:inline">{t("footer.column1.our_services")}</span>
                  </p>
                  <Link
                    href="/foretagslan"
                    className="hover:text-white transition-colors cursor-pointer"
                  >
                    {t("footer.column1.business_loans")}
                  </Link>
                  <Link
                    href="/fakturakop"
                    className="hover:text-white transition-colors cursor-pointer"
                  >
                    {t("footer.column1.invoice_purchase")}
                  </Link>
                  <Link
                    href="/inkasso"
                    className="hover:text-white transition-colors cursor-pointer"
                  >
                    {t("footer.column1.collections")}
                  </Link>
                </ul>
              </div>

              {/* Second Column */}
              <div className="text-[#EDD2B9] text-[16px]  md:text-[clamp(14px,1.0417vw,20px)]">
                <ul className="flex flex-col">
                  <p className="font-medium">{t("footer.column2.the_factory")}</p>
                  <Link
                    href="/aboutus"
                    scroll={false}
                    onClick={() => window.scrollTo({ top: 0, left: 0 })}
                    className="hover:text-white transition-colors cursor-pointer"
                  >
                    {t("footer.column2.about_us")}
                  </Link>
                  <Link
                    href="/medarbetare"
                    scroll={false}
                    onClick={(e) => handleSectionLink(e, "/medarbetare")}
                    className="hover:text-white transition-colors cursor-pointer"
                  >
                    {t("footer.column2.team")}
                  </Link>
                  <Link
                    href="/styrelse"
                    scroll={false}
                    onClick={(e) => handleSectionLink(e, "/styrelse")}
                    className="hover:text-white transition-colors cursor-pointer"
                  >
                    {t("footer.column2.board")}
                  </Link>
                  <Link
                    href="/agare"
                    scroll={false}
                    onClick={(e) => handleSectionLink(e, "/agare")}
                    className="hover:text-white transition-colors cursor-pointer"
                  >
                    {t("footer.column2.owners")}
                  </Link>
                  <Link
                    href="/fabrikens-vanner"
                    className="hover:text-white transition-colors cursor-pointer"
                  >
                    {t("footer.column2.friends")}
                  </Link>
                  <Link href="/csr" className="hover:text-white transition-colors cursor-pointer">
                    {t("footer.column2.csr")}
                  </Link>
                  <Link
                    href="/blog"
                    className="mt-4 2xl:mt-6 hover:text-white transition-colors cursor-pointer"
                  >
                    {t("footer.column2.blog")}
                  </Link>
                  <Link href="/faq" className="hover:text-white transition-colors cursor-pointer">
                    {t("footer.column2.faq")}
                  </Link>
                  <a
                    href="https://www.linkedin.com/company/finansfabriken/jobs/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-white transition-colors cursor-pointer"
                  >
                    {t("footer.column2.career")}
                  </a>
                </ul>
              </div>

              {/* Third Column - Follow Us, Contact & Legal */}
              <div className="text-[#EDD2B9] text-[16px] md:text-[clamp(14px,1.0417vw,20px)]">
                {/* Follow Us - social icons */}
                <div className="mb-4 2xl:mb-6">
                  <h3 className="font-medium"> {t("footer.column3.follow_us")} </h3>
                  <div className="flex items-center gap-3 mt-2">
                    {SOCIAL_LINKS.map(({ key, href, icon }) => (
                      <a
                        key={key}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={t(`footer.column3.${key}`)}
                        className="transition-opacity hover:opacity-80 cursor-pointer"
                      >
                        <Image
                          src={icon}
                          alt={t(`footer.column3.${key}`)}
                          width={34}
                          height={34}
                          quality={100}
                          unoptimized
                          style={{
                            width: "clamp(24px, calc(24px + (100vw - 768px) * 0.008681), 34px)",
                            height: "clamp(24px, calc(24px + (100vw - 768px) * 0.008681), 34px)"
                          }}
                        />
                      </a>
                    ))}
                  </div>
                </div>

                {/* Company Information */}
                <div className="md:mb-4 2xl:mb-6">
                  <h3 className="font-medium">{t("footer.column3.company_information")}</h3>
                  <p> {t("footer.column3.company_name")} </p>
                  <p>{t("footer.column3.registration_no")}</p>
                </div>
                <div className="mb-4 2xl:mb-6">
                  <a
                    href={`tel:${t("footer.column3.phone")}`}
                    className="hover:text-white transition-colors cursor-pointer block w-full"
                  >
                    {t("footer.column3.phone")}
                  </a>
                  <a
                    href={`mailto:${t("footer.column3.email")}`}
                    className="hover:text-white transition-colors cursor-pointer"
                  >
                    {t("footer.column3.email")}
                  </a>
                </div>

                <div className="py-8 md:hidden w-56 md:w-1/3 lg:w-1/4">
                  <Image
                    src="/Icons/Finansfabriken.svg"
                    alt="Finansfabriken"
                    className="w-full h-14.25 md:w-36 xl:w-40 2xl:w-48"
                    width={224}
                    height={57}
                  />
                </div>

                <div className="flex flex-col">
                  <Link
                    href="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-white transition-colors cursor-pointer"
                  >
                    {t("footer.column3.privacy_policy")}
                  </Link>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      Cookiebot.show();
                    }}
                    className="hover:text-white transition-colors cursor-pointer"
                  >
                    {t("footer.column3.cookies")}
                  </a>
                  <p> {t("footer.column3.copyright")} </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Footer;
